
import { useState } from "react";
import { EventObject, CalendarViewMode } from "@/components/calendar/types";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateEvent, useUpdateEvent, useDeleteEvent } from "./useEvents";

export const useCalendarState = () => {
	const [currentDate, setCurrentDate] = useState<Date>(new Date());
	const [viewMode, setViewMode] = useState<CalendarViewMode>("week");
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [selectedSlotDate, setSelectedSlotDate] = useState<Date | undefined>(undefined);
	
	const { user } = useAuth();
	const queryClient = useQueryClient();
	
	// Use the new event hooks
	const createEventMutation = useCreateEvent();
	const updateEventMutation = useUpdateEvent();
	const deleteEventMutation = useDeleteEvent();

	const navigateCalendar = (direction: "prev" | "next") => {
		const newDate = new Date(currentDate);
		if (viewMode === "day") {
			direction === "prev" ? newDate.setDate(newDate.getDate() - 1) : newDate.setDate(newDate.getDate() + 1);
		} else if (viewMode === "week") {
			direction === "prev" ? newDate.setDate(newDate.getDate() - 7) : newDate.setDate(newDate.getDate() + 7);
		} else {
			direction === "prev" ? newDate.setMonth(newDate.getMonth() - 1) : newDate.setMonth(newDate.getMonth() + 1);
		}
		setCurrentDate(newDate);
	};

	const goToToday = () => setCurrentDate(new Date());

	const handleEventClick = (event: EventObject) => {
		//console.log("Event clicked:", event);
	};

	const handleDateClick = (date: Date) => {
		setSelectedDate(date);
		if (viewMode === "month") {
			setViewMode("day");
			setCurrentDate(date);
		}
	};

	const handleEventDrop = async (eventId: string, newStart: Date, newEnd: Date) => {
		if (!user) {
			toast.error("You must be logged in to update events");
			return;
		}

		try {
			await updateEventMutation.mutateAsync({
				id: eventId,
				start_time: newStart.toISOString(),
				end_time: newEnd.toISOString()
			});
		} catch (err) {
			console.error("Error updating event:", err);
			toast.error("Failed to update event");
			throw err;
		}
	};

	const handleEventCreate = async (eventData: { 
		title: string; 
		start: Date; 
		end: Date; 
		description?: string;
		color?: string;
		location?: string;
		url?: string;
		status?: 'tentative' | 'confirmed' | 'cancelled';
		priority?: 'low' | 'medium' | 'high' | 'urgent';
		all_day?: boolean;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		};
		linkedUsers?: string[];
	}) => {
		console.log('useCalendarState handleEventCreate called with:', eventData);
		console.log('linkedUsers in useCalendarState:', eventData.linkedUsers);
		
		try {
			await createEventMutation.mutateAsync({
				title: eventData.title,
				description: eventData.description,
				start_time: eventData.start.toISOString(),
				end_time: eventData.end.toISOString(),
				all_day: eventData.all_day || false,
				color: eventData.color || '#6366f1',
				status: eventData.status || 'confirmed',
				priority: eventData.priority || 'medium',
				visibility: 'private',
				location: eventData.location,
				url: eventData.url,
				relations: eventData.relations || {},
				metadata: {},
				linkedUsers: eventData.linkedUsers
			});
			console.log('Event created successfully with linkedUsers');
		} catch (err) {
			console.error("Error in event creation:", err);
			throw err;
		}
	};

	const handleEventUpdate = async (eventId: string, eventData: { 
		title: string; 
		start: Date; 
		end: Date; 
		description?: string;
		color?: string;
		location?: string;
		url?: string;
		status?: 'tentative' | 'confirmed' | 'cancelled';
		priority?: 'low' | 'medium' | 'high' | 'urgent';
		all_day?: boolean;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		}
	}) => {
		if (!user) {
			toast.error("You must be logged in to update events");
			return;
		}

		try {
			await updateEventMutation.mutateAsync({
				id: eventId,
				title: eventData.title,
				description: eventData.description,
				start_time: eventData.start.toISOString(),
				end_time: eventData.end.toISOString(),
				all_day: eventData.all_day || false,
				color: eventData.color,
				status: eventData.status || 'confirmed',
				priority: eventData.priority || 'medium',
				location: eventData.location,
				url: eventData.url,
				relations: eventData.relations || {}
			});
		} catch (err) {
			console.error("Error in event update:", err);
			throw err;
		}
	};

	const handleEventDelete = async (eventId: string) => {
		if (!user) {
			toast.error("You must be logged in to delete events");
			return;
		}

		try {
			await deleteEventMutation.mutateAsync(eventId);
		} catch (err) {
			console.error("Error in event deletion:", err);
			throw err;
		}
	};

	const handleTimeSlotClick = (hour: number, day: Date) => {
		const slotDate = new Date(day);
		const hourPart = Math.floor(hour);
		const minutePart = Math.round((hour % 1) * 60);
		
		slotDate.setHours(hourPart);
		slotDate.setMinutes(minutePart);
		slotDate.setSeconds(0);
		
		setSelectedSlotDate(slotDate);
		setShowCreateModal(true);
	};

	return {
		currentDate,
		viewMode,
		selectedDate,
		showCreateModal,
		selectedSlotDate,
		setShowCreateModal,
		setViewMode,
		navigateCalendar,
		goToToday,
		handleEventClick,
		handleDateClick,
		handleEventDrop,
		handleEventCreate,
		handleEventUpdate,
		handleEventDelete,
		handleTimeSlotClick,
		isDeletingEvent: deleteEventMutation.isPending
	};
};
